import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Building2, CheckSquare, CalendarDays, ShieldCheck, UserRound, LogOut, CalendarHeart, DatabaseZap, Receipt, FileBarChart, Globe } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LogoSidebar } from "./Logo";
import { initials } from "@/lib/api";

const ITEMS = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, key: "dashboard", end: true },
  { to: "/clienti", label: "Clienti", icon: Building2, key: "clienti" },
  { to: "/task", label: "Task", icon: CheckSquare, key: "task" },
  { to: "/calendario", label: "Calendario", icon: CalendarDays, key: "calendario" },
  { to: "/il-mio-calendario", label: "Il mio calendario", icon: CalendarHeart, key: "mio-calendario" },
  { to: "/report-da-inviare", label: "Report da inviare", icon: FileBarChart, key: "report-bozze", badge: "drafts" },
  { to: "/amministrazione/fatture", label: "Fatture", icon: Receipt, key: "fatture" },
  { to: "/sito-web", label: "Sito web", icon: Globe, key: "sito-web", badge: "richieste" },
  { to: "/soci", label: "Soci", icon: ShieldCheck, key: "soci", admin: true },
  { to: "/amministrazione/dati", label: "Gestione dati", icon: DatabaseZap, key: "gestione-dati", admin: true },
  { to: "/profilo", label: "Profilo", icon: UserRound, key: "profilo" },
];

const linkCls = ({ isActive }) =>
  `group flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-semibold transition-[background-color,color,transform] duration-200 ${
    isActive ? "bg-[#0A1128] text-white shadow-md" : "text-[#0A1128] hover:bg-[#8FD0FB] hover:translate-x-0.5"
  }`;

export const Sidebar = ({ drawer = false }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const items = ITEMS.filter((i) => !i.admin || user?.role === "admin");
  const [drafts, setDrafts] = useState(0);
  const [richieste, setRichieste] = useState(0);
  useEffect(() => {
    const load = () => api.get("/sito/richieste/conteggio").then((r) => setRichieste(r.data.n)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    window.addEventListener("core-requests-changed", load);
    return () => { clearInterval(t); window.removeEventListener("core-requests-changed", load); };
  }, []);
  useEffect(() => {
    const load = () => api.get("/report-bozze/conteggio").then((r) => setDrafts(r.data.n)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    window.addEventListener("core-drafts-changed", load);
    return () => { clearInterval(t); window.removeEventListener("core-drafts-changed", load); };
  }, []);

  return (
    <aside className={`${drawer ? "h-full w-full pt-[calc(1.25rem+env(safe-area-inset-top))]" : "fixed inset-y-0 left-0 w-[260px] z-40 border-r border-[#5FB3EA]"} bg-[#6BBAF0] flex flex-col justify-between p-5 overflow-y-auto`} data-testid="sidebar">
      <div>
        <div className="px-1 pt-2 pb-9"><LogoSidebar /></div>
        <nav className="space-y-1">
          {items.map(({ to, label, icon: Icon, key, end, badge }) => (
            <NavLink key={key} to={to} end={end} className={linkCls} data-testid={`sidebar-nav-${key}`}>
              {({ isActive }) => (
                <>
                  <Icon className={`h-[18px] w-[18px] ${isActive ? "text-[#E8173A]" : ""}`} strokeWidth={2.2} />
                  <span className="flex-1">{label}</span>
                  {badge === "richieste" && richieste > 0 && <span className="min-w-[22px] h-[22px] px-1.5 rounded-full bg-[#E8173A] text-white text-[11px] font-bold flex items-center justify-center" data-testid="sidebar-richieste-badge">{richieste}</span>}
                  {badge === "drafts" && drafts > 0 && <span className="min-w-[22px] h-[22px] px-1.5 rounded-full bg-[#E8173A] text-white text-[11px] font-bold flex items-center justify-center" data-testid="sidebar-drafts-badge">{drafts}</span>}
                </>
              )}
            </NavLink>
          ))}
        </nav>
      </div>
      <div className="border-t border-[#0A1128]/15 pt-4">
        <div className="flex items-center gap-3 px-1 mb-3" data-testid="sidebar-user">
          <div className="h-9 w-9 rounded-full bg-[#0A1128] text-white text-xs font-bold flex items-center justify-center">
            {initials(user?.nome, user?.cognome)}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-bold text-[#0A1128] truncate">{user?.nome} {user?.cognome}</div>
            <div className="text-[11px] text-slate-500">{user?.role === "admin" ? "Amministratore" : "Socio"}</div>
          </div>
        </div>
        <button
          onClick={async () => { await logout(); navigate("/login"); }}
          className="w-full flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-semibold text-[#0A1128] hover:bg-[#E8173A] hover:text-white transition-colors duration-200"
          data-testid="sidebar-logout-button"
        >
          <LogOut className="h-[18px] w-[18px]" /> Esci
        </button>
      </div>
    </aside>
  );
};
